import { EuiHighlight, EuiText } from '@elastic/eui'
import { FC, useCallback, useEffect, useState } from 'react'
import SelectionBox, { SelectionBoxProps } from './SelectionBox'

import { SearchQueryProps } from '@hooks/query/useSearchQuery'
import { SelectionBoxShowMoreItem } from './SelectionBoxShowMoreItem'
import { useSearchQuery } from '@hooks/query'

const SHOW_MORE_KEY = 'SHOW_MORE'

type UseQueryArgs = Parameters<typeof useSearchQuery>

export type RemoteSelectionBoxProps = Omit<SelectionBoxProps, 'options'> & {
    hook: UseQueryArgs[0]
    config: UseQueryArgs[1]
    getHelpText?: (item: any) => string
}

const getDefaultQueryParams = (config: SearchQueryProps, defaultValue: any) => {
    const defaultParams = { ...config }

    if (config.append) {
        defaultParams.append = {
            ...config.append,
            page: 1,
        }
    } else {
        defaultParams.append = { page: 1 }
    }

    if (defaultValue) {
        defaultParams.append.filters = [
            ...(defaultParams.append?.filters || []),
            `id==${defaultValue}`,
        ]
    }

    return defaultParams
}

const RemoteSelectionBox: FC<RemoteSelectionBoxProps> = (props) => {
    const { hook, config, value, getHelpText, ...selectionBoxProps } = props

    const [options, setOptions] = useState<
        { key: string; label: string; value: any; helpText?: string }[]
    >([])
    const [nextPage, setNextPage] = useState<{
        hasMore: boolean
        nextLink?: string
    }>({
        hasMore: true,
    })
    const [selected, setSelected] = useState<
        { label: string; value: string }[]
    >([])
    const [queryParams, setQueryParams] = useState<SearchQueryProps>(
        getDefaultQueryParams(config, props.multiple ? undefined : value)
    )
    const [loading, setLoading] = useState<boolean>(true)
    const [showMoreLoading, setShowMoreLoading] = useState<boolean>(false)

    const { data, isLoading } = useSearchQuery<any>(hook, queryParams)

    const handleOnSearchChange = (searchValue?: string) => {
        if (searchValue === queryParams.search.value) return

        setLoading(true)
        setOptions([])
        setQueryParams((prev) => ({
            ...getDefaultQueryParams(config, value),
            search: {
                ...config.search,
                value: searchValue ? searchValue : undefined,
            },
        }))
    }

    const handleOnFetchNext = () => {
        if (nextPage.hasMore) {
            setShowMoreLoading(true)
            setQueryParams((prev) => ({
                ...prev,
                append: {
                    ...prev.append,
                    page: (prev.append?.page || 1) + 1,
                    nextLink: nextPage.nextLink,
                },
            }))
        }
    }

    const handleOnRender = (option: any, search?: string) => {
        const { label, value, helpText } = option

        if (value === SHOW_MORE_KEY)
            return (
                <SelectionBoxShowMoreItem
                    onClick={() => handleOnFetchNext()}
                    isLoading={showMoreLoading}
                />
            )
        else
            return (
                <>
                    <EuiHighlight search={search || []}>{label}</EuiHighlight>
                    {helpText && <EuiText size="xs">{helpText}</EuiText>}
                </>
            )
    }

    const handleOnChange: SelectionBoxProps['onChange'] = (val) => {
        if (val?.[0] === SHOW_MORE_KEY) return
        setSelected(val)
        selectionBoxProps.onChange(val)
    }

    const getOptions = useCallback(() => {
        return nextPage.hasMore
            ? [...options, { label: SHOW_MORE_KEY, value: SHOW_MORE_KEY }]
            : options
    }, [nextPage])

    useEffect(() => {
        if (isLoading) return

        let newOptions = [...options]
        data?.items?.forEach((item) => {
            if (newOptions.findIndex((x) => x.value === item.id) < 0) {
                newOptions = [
                    ...newOptions,
                    {
                        key: item.id.toString(),
                        label: item.name,
                        value: item.id,
                        helpText: getHelpText?.(item),
                    },
                ]
            }
        })

        if (
            (data?.totalCount && data.totalCount > newOptions.length) ||
            data?.odataNextLink
        ) {
            setNextPage({
                hasMore: true,
                nextLink: data?.odataNextLink,
            })
        } else {
            setNextPage({ hasMore: false })
        }

        setOptions(newOptions)
        setLoading(false)
        setShowMoreLoading(false)
    }, [data, isLoading])

    useEffect(() => {
        if (data?.items && !selected?.length) {
            let newValue
            if (Array.isArray(value))
                newValue = value.map((x) => ({ value: x.id, label: x.name }))
            else
                newValue = data.items
                    .filter((item) => item.id === value)
                    .map((item) => ({
                        key: item.id.toString(),
                        label: item.name || '',
                        value: item.id,
                        helpText: getHelpText?.(item),
                    }))

            setSelected(newValue)
        }
    }, [value, data])

    return (
        <SelectionBox
            {...selectionBoxProps}
            async={true}
            rowHeight={getHelpText ? 50 : undefined}
            options={getOptions()}
            loading={loading}
            renderOption={handleOnRender}
            optionsAsValue={selected}
            onChange={handleOnChange}
            onSearchChange={handleOnSearchChange}
        />
    )
}

export default RemoteSelectionBox
